import { ChangeEvent, forwardRef, PropsWithChildren, useMemo, useRef } from 'react';
import { useOnClickOutside } from 'usehooks-ts';
import {
    ACCORDION_PADDING,
    MIN_OPTION_HEIGHT,
    MIN_OPTION_VISIBLE,
} from '../input-autocomplete/input-autocomplete.constants';
import { getEndButtonDefinition, getStartButtonDefinition } from '../input-autocomplete/input-autocomplete.utils';
import TextField from '../text-field/textField';

interface InputAutocompleteSimpleLayoutI {
    name: string;
    label?: string;
    show: boolean;
    query: string;
    error?: boolean;
    testId: string;
    isLoading: boolean;
    minVisible: number;
    optionsLength: number;
    optionsHeight: number;
    errorMessage?: string;
    placeholder?: string;
    containerClasses?: string;
    inputClassName?: string;
    handleShow(value?: boolean): void;
    optionsContainerClasses?: string;
    disabled?: boolean;
    handleInputChange(event: ChangeEvent<HTMLInputElement>): void;
    required?: boolean;
    id?: string;
    onClick?(): void; // to handle click on the input
    onFocus?(): void; // to handle focus on the input
    onBlur?(event: React.FocusEvent<HTMLInputElement>): void; // to handle blur on the input
}

export const InputAutocompleteSimpleLayout = forwardRef<
    HTMLDivElement,
    PropsWithChildren<InputAutocompleteSimpleLayoutI>
>(
    (
        {
            show,
            name,
            label,
            query,
            error = false,
            testId,
            isLoading,
            minVisible = MIN_OPTION_VISIBLE,
            optionsLength,
            optionsHeight,
            handleShow,
            placeholder,
            errorMessage,
            containerClasses,
            inputClassName,
            handleInputChange,
            optionsContainerClasses,
            children,
            disabled,
            required,
            onClick,
            onFocus,
            onBlur,
            ...props
        },
        ref,
    ) => {
        const refMain = useRef<HTMLDivElement | null>(null);
        const inputRef = useRef<HTMLInputElement | null>(null);

        // Handle click outside
        useOnClickOutside(refMain, () => handleShow(false));

        //  glass start button
        const startButton = useMemo(() => {
            return getStartButtonDefinition({ show });
        }, [show]);

        // arrow and load end button
        const endButton = useMemo(() => {
            return getEndButtonDefinition({
                show,
                isLoading,
                action: () => {
                    handleShow();
                    inputRef.current?.focus();
                },
            });
        }, [show, isLoading, handleShow]);

        const dynamicMinOptions = useMemo(() => {
            const min = optionsLength < MIN_OPTION_VISIBLE ? minVisible : MIN_OPTION_VISIBLE;
            return min;
        }, [minVisible, optionsLength]);

        const dynamicHeight = useMemo(() => {
            if (!show) return 0;

            const heightWithOptions = MIN_OPTION_HEIGHT * dynamicMinOptions + ACCORDION_PADDING * 2;
            return optionsLength ? heightWithOptions : 0;
        }, [show, optionsLength, dynamicMinOptions]);

        return (
            <div
                className={`relative ${containerClasses || ''}`}
                data-testid={testId}
                data-test-id={testId}
                ref={refMain}
            >
                {disabled ? (
                    <TextField
                        {...props}
                        label={label}
                        name={name}
                        value={query}
                        dataTestId={`${name}-combo-input`}
                        disabled={true}
                        required={required}
                        className={inputClassName}
                    />
                ) : (
                    <TextField
                        {...props}
                        placeholder={placeholder}
                        label={label}
                        name={name}
                        error={error}
                        errorMessage={errorMessage}
                        ref={inputRef}
                        value={query}
                        className={inputClassName}
                        onChange={handleInputChange}
                        endButton={endButton}
                        startButton={startButton}
                        dataTestId={`${name}-combo-input`}
                        onClick={() => {
                            handleShow();
                            onClick?.();
                        }}
                        onFocus={onFocus}
                        onBlur={(e) => {
                            onBlur?.(e as React.FocusEvent<HTMLInputElement>);
                        }}
                        required={required}
                    />
                )}

                <div
                    ref={ref}
                    className={optionsContainerClasses}
                    style={
                        dynamicHeight === 0
                            ? { height: 0, padding: 0, border: 'none' }
                            : { height: dynamicHeight, display: 'block' }
                    }
                    data-testid={`${name}-autocomplete-container`}
                >
                    <div
                        className="leading-none"
                        style={{
                            height: `${optionsHeight}px`,
                            width: '100%',
                            position: 'relative',
                        }}
                    >
                        {children}
                    </div>
                </div>
            </div>
        );
    },
);

export default InputAutocompleteSimpleLayout;
